import { IDocumentTracking } from '../types/document-tracking';
import { ITrackingData } from '../types/tracking-data';
import { ITrackingStats } from '../types/tracking-stats';
import { IPageGraphData } from '@/document-view/tracking/graph/types/page-graph-data';
import { IClickTracking } from '@/document-view/tracking/types/click-tracking';
import { IPageTracking } from '@/document-view/tracking/types/page-tracking';
import { IVideoTracking } from '@/document-view/tracking/types/video-tracking';
import { IVisitTracking } from '@/document-view/tracking/types/visit-tracking';
import { EDocumentPageType } from '@lib-shared/enums/document-page-type';
import { IDocumentPage } from '@lib-shared/types/document-page';
import { IRecipient } from '@lib-shared/types/Recipient';
import { IDocument } from '@lib-shared/types/Document';

export class TrackingHelper {
  static getThumbUrl(page: IDocumentPage): string {
    switch (page.pageType) {
      case EDocumentPageType.ContentLink:
        return page.contentLinkBlock.thumbUrl;
      case EDocumentPageType.DocumentContent:
        return page.thumbUrl;
      case EDocumentPageType.Video:
        return page.video.thumbUrl;
      default:
        return '';
    }
  }

  static getRecipientLastVisitTime(recipientVisitTimes: IVisitTracking[]): string {
    if (!recipientVisitTimes.length) {
      return null;
    }
    const sortedRecipientVisitTimes: IVisitTracking[] = [...recipientVisitTimes].sort(
      (a: IVisitTracking, b: IVisitTracking) =>
        new Date(b.visitTime).getTime() - new Date(a.visitTime).getTime()
    );
    return sortedRecipientVisitTimes[0].visitTime;
  }

  static getContentLinkClicks(
    contentLinkBlockId: string,
    clickTrackings: IClickTracking[],
    recipientId?: string
  ): number {
    let filteredClickTrackings: IClickTracking[] = clickTrackings;
    if (recipientId) {
      filteredClickTrackings = clickTrackings.filter(
        (trackClick: IClickTracking) => trackClick.recipientId === recipientId
      );
    }
    return filteredClickTrackings.filter(
      (trackClick: IClickTracking) => trackClick.contentLinkBlockId === contentLinkBlockId
    ).length;
  }

  static getPageGraphDataList(
    clickTrackings: IClickTracking[],
    videoTrackings: IVideoTracking[],
    pageTrackings: IPageTracking[],
    documentPages: IDocumentPage[],
    recipientId?: string
  ): IPageGraphData[] {
    const pageNumToPageTime: Map<number, number> = this.getPageTimeByPage(
      documentPages,
      videoTrackings,
      pageTrackings,
      recipientId
    );
    return documentPages.map((page: IDocumentPage) => ({
      pageId: page.id,
      pageNum: page.pageNum,
      pageType: page.pageType,
      viewTime: pageNumToPageTime.get(page.pageNum),
      metadata: {
        thumbUrl: this.getThumbUrl(page),
        videoUrl: page.pageType === EDocumentPageType.Video ? page.video.videoUrl : null,
        contentLinkClicks:
          page.pageType === EDocumentPageType.ContentLink
            ? this.getContentLinkClicks(page.contentLinkBlock.id, clickTrackings, recipientId)
            : null,
        editorPdfPageCount:
          page.pageType === EDocumentPageType.Editor ? page.editorBlock.pdfPageCount : null,
      },
    }));
  }

  static getPageTimeByPage(
    pages: IDocumentPage[],
    videoTrackings: IVideoTracking[],
    pageTrackings: IPageTracking[],
    recipientId?: string
  ): Map<number, number> {
    const pageNumToPageTime: Map<number, number> = new Map();

    for (const page of pages) {
      switch (page.pageType) {
        case EDocumentPageType.DocumentContent:
        case EDocumentPageType.ContentLink:
        case EDocumentPageType.Editor:
          pageNumToPageTime.set(
            page.pageNum,
            this.getPageTime(page.pageNum, pageTrackings, recipientId)
          );
          break;
        case EDocumentPageType.Video:
          pageNumToPageTime.set(
            page.pageNum,
            this.getVideoTime(page.video.id, videoTrackings, recipientId)
          );
          break;
      }
    }
    return pageNumToPageTime;
  }

  static getPageTime(
    pageNum: number,
    pageTrackings: IPageTracking[],
    recipientId?: string
  ): number {
    let filteredPageTrackings: IPageTracking[] = pageTrackings;
    if (recipientId) {
      filteredPageTrackings = pageTrackings.filter(
        (trackPage: IPageTracking) => trackPage.recipientId === recipientId
      );
    }
    return filteredPageTrackings
      .filter((trackPage: IPageTracking) => trackPage.pageNum === pageNum)
      .map((trackPage: IPageTracking) => trackPage.pageTime)
      .reduce((a: number, b: number) => a + b, 0);
  }

  static getVideoTime(
    videoId: string,
    videoTrackings: IVideoTracking[],
    recipientId?: string
  ): number {
    let filteredVideoTrackings: IVideoTracking[] = videoTrackings;
    if (recipientId) {
      filteredVideoTrackings = videoTrackings.filter(
        (trackVideo: IVideoTracking) => trackVideo.recipientId === recipientId
      );
    }
    return filteredVideoTrackings
      .filter((videoTrack: IVideoTracking) => videoTrack.videoId === videoId)
      .map((videoTrack: IVideoTracking) => videoTrack.viewDuration)
      .reduce((a: number, b: number) => a + b, 0);
  }

  static getUpdatedPageTrackings(
    existingTrackings: IPageTracking[],
    newPageTracking: IPageTracking
  ): IPageTracking[] {
    const updatedPageTrackings: IPageTracking[] = [...existingTrackings];
    const trackPageIndex: number = updatedPageTrackings.findIndex(
      ({ id }: IPageTracking) => id === newPageTracking.id
    );

    if (trackPageIndex > -1) {
      updatedPageTrackings[trackPageIndex] = newPageTracking;
    } else {
      updatedPageTrackings.push(newPageTracking);
    }
    return updatedPageTrackings;
  }

  static recipientsWithTracking(document: IDocument, tracking: IDocumentTracking) {
    const { recipients, pages } = document;
    return recipients.map((recipient: IRecipient) => {
      const { clickTrackings, videoTrackings, pageTrackings, visitTrackings } = tracking;
      const recipientPageTimes: IPageGraphData[] = TrackingHelper.getPageGraphDataList(
        clickTrackings,
        videoTrackings,
        pageTrackings,
        pages,
        recipient.id
      );

      const recipientVisits: IVisitTracking[] = visitTrackings.filter(
        (visitTracking: IVisitTracking) => visitTracking.recipientId === recipient.id
      );

      return {
        ...recipient,
        recipientVisitTime: recipientPageTimes
          .map((pageTime: IPageGraphData) => pageTime.viewTime)
          .reduce((a: number, b: number) => a + b, 0)
          .toString(),
        recipientLastVisitTime: this.getRecipientLastVisitTime(recipientVisits),
        recipientVisitCount: `${recipientVisits.length}`,
      };
    });
  }
  static filteredTracking(
    document: IDocument,
    tracking: IDocumentTracking,
    selectedRecipientId: string
  ): ITrackingData {
    const { pages, video } = document;
    if (!pages.length) {
      return null;
    }
    const { clickTrackings = [], videoTrackings, pageTrackings } = tracking;
    const pageGraphDataList: IPageGraphData[] = this.getPageGraphDataList(
      clickTrackings,
      videoTrackings,
      pageTrackings,
      pages,
      selectedRecipientId
    );

    const totalViewTime: number = pageGraphDataList
      .map((page: IPageGraphData) => page.viewTime)
      .reduce((a: number, b: number) => a + b, 0);

    if (!video?.video) {
      return {
        engagementVideoTracking: undefined,
        pageGraphDataList,
        totalViewTime: totalViewTime,
      };
    }

    const { id: engagementVideoId, videoUrl, thumbUrl } = video.video;
    const engagementVideoViewDuration = this.getVideoTime(
      engagementVideoId,
      videoTrackings,
      selectedRecipientId
    );
    return {
      engagementVideoTracking: {
        videoUrl,
        thumbUrl,
        viewDuration: engagementVideoViewDuration,
      },
      pageGraphDataList,
      totalViewTime: totalViewTime + engagementVideoViewDuration,
    };
  }
  static trackingStats(filteredTracking, tracking): ITrackingStats {
    const { totalViewTime } = filteredTracking;
    const visitTrackings = tracking?.visitTrackings || [];
    const documentVisitCount = visitTrackings.length;
    const uniqueViewerCount = new Set(
      visitTrackings.map((trackVisit: IVisitTracking) => trackVisit.recipientId)
    ).size;

    return { documentVisitCount, documentVisitTime: totalViewTime, uniqueViewerCount };
  }
}
